import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { TargetType, TimeLine } from '../generated/graphql'
import { useGetHistoryLazyQuery } from '../generated/graphql'
import { useReplayStore } from '../services/snapshots/replay.store.hook'

// Constants
export const ITEMS_PER_PAGE = 15

// Types
export type TimeLineItem = Partial<TimeLine> & {
  chunk: number
  loading: boolean
}

interface ReplayContextType {
  // Target information
  nodeId: string
  resetReplay: (nodeId: string, initialIndex?: number) => void

  // Items
  totalItems: number
  timelineItems: TimeLine[]

  // Loading states
  loading: boolean
  loadingChunk: boolean

  // Playback state
  isPlaying: boolean
  sliderTick: number

  // Playback controls
  startPlayback: () => void
  stopPlayback: () => void
  seekToIndex: (
    index: number,
    direction?: 'forward' | 'backwards'
  ) => Promise<void>
  setFilter: (filter: { type: TargetType; id: string }) => void
  // Navigation
  goForward: () => void
  goBackward: () => void
}

interface ReplayProviderProps {
  children: React.ReactNode
  initialTargetId?: string
  initialTargetType?: TargetType
}

// Create context
const ReplayContext = createContext<ReplayContextType | undefined>(undefined)

export const ReplayProvider: React.FC<ReplayProviderProps> = ({ children }) => {
  // Reducer
  const {
    chunks,
    totalItems,
    totalChunks,
    nodeId,
    setChunks,
    setTotalItems,
    setTotalChunks,
    setNodeId,
  } = useReplayStore()

  // Target state

  // Data state
  const [loadingChunk, setLoadingChunk] = useState<boolean>(false)
  // Filter: if set we move to the next item that matches the filter in the timeline
  const [filter, setFilter] = useState<{
    type: TargetType
    id: string
  }>({
    type: TargetType.Node,
    id: nodeId,
  })
  // Playback state
  const [isPlaying, setIsPlaying] = useState(false)
  const [sliderTick, setSliderTick] = useState(0)

  // GraphQL query
  const [getHistory, { loading }] = useGetHistoryLazyQuery()

  // In this context timeline items are all the chunks loaded sequencially
  const timelineItems = useMemo(() => {
    if (!chunks) {
      return []
    }
    const firstChunkNotLoaded = chunks.findIndex((chunk) => chunk?.[0]?.loading)
    const items =
      firstChunkNotLoaded > 0
        ? chunks.slice(0, firstChunkNotLoaded).flat()
        : chunks.flat()
    // We can safely cast to timeline item because we collected the ones already loaded
    return items as TimeLine[]
  }, [chunks])

  // Query constructor helper - request oldest items first (ascending order)
  const getChunk = useCallback(
    (page: number, optional: { nodeId?: string } = {}) => ({
      variables: {
        input: {
          targetId: optional.nodeId || nodeId,
          targetType: TargetType.Node,
          limit: ITEMS_PER_PAGE,
          offset: page * ITEMS_PER_PAGE,
          sort: 'asc',
        },
      },
    }),
    [nodeId]
  )

  // Same as _loadChunk but without setState
  const _loadChunk = useCallback(
    (nodeId: string, page: number): Promise<Array<TimeLineItem>> => {
      if (!nodeId) return Promise.resolve([])

      return new Promise((resolve) => {
        getHistory(getChunk(page))
          .then(({ data }) => {
            const newChunks = data?.getHistory.results.map((item) => ({
              ...item,
              chunk: page,
              loading: false,
            })) as Array<TimeLineItem>
            resolve(newChunks)
          })
          .catch(() => {
            resolve([])
          })
      })
    },
    [getHistory, getChunk]
  )
  // Load a specific chunk of timeline data
  const loadChunk = useCallback(
    (
      chunks: readonly TimeLineItem[][],
      page: number
    ): Promise<Array<TimeLineItem>> => {
      if (!nodeId) return Promise.resolve([])

      setLoadingChunk(true)

      return _loadChunk(nodeId, page)
        .then((newChunks) => {
          const updatedChunks = [...chunks]
          updatedChunks[page] = newChunks
          setChunks(updatedChunks)
          setLoadingChunk(false)
          return newChunks
        })
        .catch(() => {
          setLoadingChunk(false)
          return []
        })
    },
    [_loadChunk, setChunks, setLoadingChunk, nodeId]
  )

  const _loadChunksUntilIndex = useCallback(
    async (
      chunks: readonly TimeLineItem[][],
      index: number
    ): Promise<TimeLineItem[][]> => {
      const jumpToChunk = Math.floor(index / ITEMS_PER_PAGE)
      const missingChunks = chunks.reduce((acc, chunk, chunkIndex) => {
        // Index 0 is always init on reset
        if (chunkIndex === 0) {
          return acc
        }
        if (chunkIndex <= jumpToChunk && chunk[0]?.loading) {
          acc.push(chunkIndex)
        }
        return acc
      }, [] as number[])

      // Chunks come from the reducer can not be mutated
      const updatedChunks = [...chunks]

      await Promise.all(
        missingChunks.map(async (chunkNo) => {
          updatedChunks[chunkNo] = await _loadChunk(nodeId, chunkNo)
        })
      )

      return updatedChunks
    },
    [_loadChunk, nodeId]
  )

  // Initialize with first chunk (oldest items) when target changes
  const initialize = useCallback(
    async (nodeId: string, initialIndex: number = 0) => {
      const { data } = await getHistory(getChunk(0, { nodeId }))
      if (data?.getHistory.results.length === 0) {
        return []
      }
      // Calculate total pages
      const totalItems = data?.getHistory.total || 0
      const totalChunks = Math.ceil(totalItems / ITEMS_PER_PAGE)
      setTotalItems(totalItems)
      setTotalChunks(totalChunks)

      // Initialize chunk array with loading placeholders
      const initialChunks = Array.from(
        { length: totalChunks },
        (_, index) => [{ loading: true, chunk: index }] as TimeLineItem[]
      )

      // Set first chunk with actual data (oldest items)
      if (data?.getHistory.results) {
        initialChunks[0] = data.getHistory.results.map((item) => ({
          ...item,
          chunk: 0,
          loading: false,
        })) as TimeLineItem[]
      }

      const jumpToChunk = Math.floor(initialIndex / ITEMS_PER_PAGE)
      if (initialIndex && jumpToChunk > 0) {
        const updatedChunks = await _loadChunksUntilIndex(
          initialChunks,
          initialIndex
        )
        setChunks(updatedChunks)
        return updatedChunks
      }

      setChunks(initialChunks)
      return initialChunks
    },
    [getHistory, getChunk, loadChunk]
  )

  // Playback control handlers
  const startPlayback = useCallback(() => {
    // If at the end, start from beginning
    if (sliderTick >= timelineItems.length - 1) {
      setSliderTick(0)
    }
    setIsPlaying(true)
  }, [sliderTick, timelineItems.length])

  const stopPlayback = useCallback(() => {
    setIsPlaying(false)
  }, [])

  /**
   * When we move with filter, we need to jump to the next item that matches the filter
   * But this may not be loaded yet, we check chunk by chunk until we find it
   */
  const seekNextIndexWithFilter = useCallback(
    async (index: number, chunk: TimeLineItem[]) => {
      const _next = async (index: number, chunk: TimeLineItem[]) => {
        const jumpToChunk = Math.floor(index / ITEMS_PER_PAGE)

        const data = chunk?.[0]?.loading
          ? // This function is also updating the cache values
            await loadChunk(chunks, jumpToChunk)
          : chunk.flat()

        // Search for the item in the current chunk
        const item = data
          .slice(index % ITEMS_PER_PAGE)
          .find((item) => item.tree?.some((node) => node.id === filter.id))

        if (item) {
          return jumpToChunk * ITEMS_PER_PAGE + data.indexOf(item)
        }

        // If not found in current chunk and there are more chunks, try next chunk
        if (jumpToChunk + 1 < totalChunks) {
          return _next(
            (jumpToChunk + 1) * ITEMS_PER_PAGE,
            chunks[jumpToChunk + 1]
          )
        }

        return totalItems - 1 // No more chunks to search, load is finished
      }

      return _next(index, chunk)
    },
    [chunks, loadChunk, totalChunks, filter]
  )

  const seekToIndex = useCallback(
    async (
      index: number,
      chunks: readonly TimeLineItem[][],
      direction: 'forward' | 'backwards' = 'forward'
    ) => {
      // Not initialized yet
      if (!chunks.length) {
        return
      }

      const updatedChunks = await _loadChunksUntilIndex(chunks, index)

      // First of last index are always accepted
      if (index === 0 || index >= totalItems) {
        setSliderTick(index)
        return
      }

      if (direction === 'backwards') {
        const item = timelineItems
          .slice(0, index + 1)
          .toReversed()
          .find((item) => item.tree.some((node) => node.id === filter.id))

        setSliderTick(item ? timelineItems.indexOf(item) : 0)
        return
      }

      // Forward direction
      const jumpToChunk = Math.floor(index / ITEMS_PER_PAGE)
      const nextTick = await seekNextIndexWithFilter(index, chunks[jumpToChunk])

      setSliderTick(nextTick)
      setChunks(updatedChunks)
    },
    [loadChunk, filter]
  )

  const goForward = useCallback(() => {
    const newIndex = Math.min(sliderTick + 1, timelineItems.length - 1)
    setSliderTick(newIndex)
    if (!isPlaying) {
      seekToIndex(newIndex, chunks, 'forward')
    }
  }, [sliderTick, timelineItems.length, isPlaying, seekToIndex])

  // Navigation handlers - move backward in time (to older items)
  const goBackward = useCallback(() => {
    const newIndex = Math.max(0, sliderTick - 1)
    setSliderTick(newIndex)
    if (!isPlaying) {
      seekToIndex(newIndex, chunks, 'backwards')
    }
  }, [sliderTick, isPlaying, seekToIndex])

  // Playback progression effect
  useEffect(() => {
    if (!isPlaying) return

    // Preload next chunks for smoother playback
    const nextChunk = Math.floor((sliderTick + 10) / ITEMS_PER_PAGE)
    if (nextChunk < totalChunks && chunks[nextChunk]?.[0]?.loading) {
      loadChunk(chunks, nextChunk)
    }

    // Advance to next item after delay
    const timer = setTimeout(() => {
      if (loadingChunk) {
        return
      }
      // Wait for data if loading
      const currentChunk = Math.floor(sliderTick / ITEMS_PER_PAGE)
      if (chunks[currentChunk]?.[0]?.loading) {
        return
      }

      if (sliderTick >= timelineItems.length - 1) {
        setIsPlaying(false)
        return
      }
      seekToIndex(sliderTick + 1, chunks)
    }, 1000) // 1 second between items

    return () => clearTimeout(timer)
  }, [
    isPlaying,
    sliderTick,
    chunks,
    timelineItems,
    loadChunk,
    totalChunks,
    loadingChunk,
  ])

  // Handle setting a new target
  const resetReplay = useCallback(
    async (newNodeId: string, initialIndex?: number) => {
      if (newNodeId !== nodeId || !chunks.length) {
        setNodeId(newNodeId)
        await initialize(newNodeId, initialIndex)
      }

      // Let the chunks to load on the state

      setSliderTick(initialIndex || 0)
      setIsPlaying(false)
    },
    [initialize, nodeId, chunks]
  )

  /**
   * Load previous when jumping to an index
   */
  useEffect(() => {
    if (!chunks) {
      return
    }
    const currentChunk = Math.floor(sliderTick / ITEMS_PER_PAGE)
    // We assume 0 is always loaded
    const pastChunks = chunks
      .slice(1, currentChunk)
      .filter((chunk) => chunk[0]?.loading)

    if (pastChunks.length > 0) {
      setLoadingChunk(true)
      Promise.all(
        pastChunks.map((chunk) => loadChunk(chunks, chunk[0].chunk))
      ).then(() => {
        setLoadingChunk(false)
      })
    }
  }, [sliderTick, chunks, loadChunk])

  const externalSeekToIndex = useCallback(
    async (index: number) => {
      await seekToIndex(index, chunks)
    },
    [seekToIndex, chunks]
  )
  const contextValue = {
    // Data
    chunks,
    timelineItems,
    totalItems,

    // Target information
    nodeId,
    resetReplay,

    // Loading states
    loading,
    loadingChunk,

    // Playback state
    isPlaying,
    sliderTick,

    // Playback controls
    startPlayback,
    stopPlayback,
    seekToIndex: externalSeekToIndex,
    setFilter,

    // Navigation
    goForward,
    goBackward,
  }
  return (
    <ReplayContext.Provider value={contextValue}>
      {children}
    </ReplayContext.Provider>
  )
}

// Hook to use the context
export const useReplayContext = (nodeId: string) => {
  const context = useContext(ReplayContext)
  if (context === undefined) {
    throw new Error('useReplayContext must be used within a ReplayProvider')
  }
  return context
}
